import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { TerminalList } from "src/app/features/Terminal/types/TerminalList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayEnumValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const TerminalsTable: FC<ResourceTableProps<TerminalList>> = ({ onStateChanged, onRowSelectionChange, datasource, resource }) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<TerminalList>): ExtendedColumnsType<TerminalList>[] => {
            return [
                {
                    title: t("terminal.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("terminal.terminalId"),
                    key: "terminalId",
                    dataIndex: "terminalId",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "terminalId" ? sortInfo.order : null,
                },
                {
                    title: t("terminal.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("terminal.terminalType"),
                    key: "terminalType",
                    dataIndex: "terminalType",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "terminalType" ? sortInfo.order : null,
                    render: (value) => displayEnumValue(value, "terminal-type"),
                },
                {
                    title: t("terminal.organizationUnit.name"),
                    key: "siteName",
                    dataIndex: "siteName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "siteName" ? sortInfo.order : null,
                },
                {
                    title: t("terminal.organizationUnit.city"),
                    key: "city",
                    dataIndex: "city",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "city" ? sortInfo.order : null,
                },
                {
                    title: t("terminal.terminalProfile.name"),
                    key: "terminalProfileName",
                    dataIndex: "terminalProfileName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "terminalProfileName" ? sortInfo.order : null,
                },
                {
                    title: t("terminal.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("terminal.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();

    const onRowClick = (record: TerminalList) => {
        navigate(`${BASE_PATH}/${ResourceName.TERMINALS}/${FORM_ACTION.EDIT}/${record.id}`);
    };

    return (
        (datasource && (
            <GenericTable<TerminalList>
                name="terminals_table"
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: TerminalList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
